// @flow
import { of } from 'rxjs';
import { debounceTime, switchMap, takeUntil, map, catchError } from 'rxjs/operators';
import { getPaymentsMadeApi, getPaymentsReceivedApi } from './api';
import { setNewAlert } from '../alert/duck';
import { ofType, StateObservable } from 'redux-observable';
import Paginator from '../../common/Paginator';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../error';
import type { Invoice } from '../../entities';
import type { RootState } from '../index';

export type PaymentsAction = {
  +type: string;
  +paymentsMade?: Invoice[];
  +fetchPaymentsMadeError?: HTTPError;
  +paymentsReceived?: Invoice[];
  +queryMadeFilter?: { [key: string]: string };
  +fetchPaymentsReceivedError?: HTTPError;
  +queryReceivedFilter?: { [key: string]: string };
  +resetPagination?: boolean;
};

export type PaymentsState = {
  +isFetchingPaymentsMade?: boolean;
  +paymentsMade?: Invoice[];
  +paymentsMadePage?: number;
  +paymentsMadePageCanAsk?: boolean;
  +fetchPaymentsMadeError?: HTTPError;

  +isFetchingPaymentsReceived?: boolean;
  +paymentsReceived?: Invoice[];
  +paymentsReceivedPage?: number;
  +paymentsReceivedPageCanAsk?: boolean;
  +fetchPaymentsReceivedError?: HTTPError;
};

// Actions
const PAYMENT_RESET_STATE = 'PAYMENT_RESET_STATE';
const PAYMENT_FETCH_PAYMENTS_MADE_REQUEST = 'PAYMENT_FETCH_PAYMENTS_MADE_REQUEST';
const PAYMENT_FETCH_PAYMENTS_MADE_SUCCESS = 'PAYMENT_FETCH_PAYMENTS_MADE_SUCCESS';
const PAYMENT_FETCH_PAYMENTS_MADE_ERROR = 'PAYMENT_FETCH_PAYMENTS_MADE_ERROR';
const PAYMENT_FETCH_PAYMENTS_RECEIVED_REQUEST = 'PAYMENT_FETCH_PAYMENTS_RECEIVED_REQUEST';
const PAYMENT_FETCH_PAYMENTS_RECEIVED_SUCCESS = 'PAYMENT_FETCH_PAYMENTS_RECEIVED_SUCCESS';
const PAYMENT_FETCH_PAYMENTS_RECEIVED_ERROR = 'PAYMENT_FETCH_PAYMENTS_RECEIVED_ERROR';

// Action creators
export function fetchPaymentsMade(queryMadeFilter?: { [key: string]: string } = {},
                                  resetPagination?: boolean): PaymentsAction {
  return {
    type: PAYMENT_FETCH_PAYMENTS_MADE_REQUEST,
    queryMadeFilter,
    resetPagination,
  }
}

export function fetchPaymentsMadeSuccess(paymentsMade: Invoice[]): PaymentsAction {
  return {
    type: PAYMENT_FETCH_PAYMENTS_MADE_SUCCESS,
    paymentsMade,
  }
}

export function fetchPaymentsMadeErrorFn(fetchPaymentsMadeError: HTTPError): PaymentsAction {
  return {
    type: PAYMENT_FETCH_PAYMENTS_MADE_ERROR,
    fetchPaymentsMadeError,
  }
}

export function fetchPaymentsReceived(queryReceivedFilter?: { [key: string]: string } = {},
                                      resetPagination?: boolean): PaymentsAction {
  return {
    type: PAYMENT_FETCH_PAYMENTS_RECEIVED_REQUEST,
    queryReceivedFilter,
    resetPagination,
  }
}

export function fetchPaymentsReceivedSuccess(paymentsReceived: Invoice[]): PaymentsAction {
  return {
    type: PAYMENT_FETCH_PAYMENTS_RECEIVED_SUCCESS,
    paymentsReceived,
  }
}

export function fetchPaymentsReceivedErrorFn(fetchPaymentsReceivedError: HTTPError): PaymentsAction {
  return {
    type: PAYMENT_FETCH_PAYMENTS_RECEIVED_ERROR,
    fetchPaymentsReceivedError,
  }
}

export function resetPaymentsState(): PaymentsAction {
  return {
    type: PAYMENT_RESET_STATE,
  }
}

export const actions = {
  PAYMENT_FETCH_PAYMENTS_MADE_REQUEST,
  PAYMENT_FETCH_PAYMENTS_MADE_SUCCESS,
  PAYMENT_FETCH_PAYMENTS_MADE_ERROR,
  PAYMENT_FETCH_PAYMENTS_RECEIVED_REQUEST,
  PAYMENT_FETCH_PAYMENTS_RECEIVED_SUCCESS,
  PAYMENT_FETCH_PAYMENTS_RECEIVED_ERROR,
  PAYMENT_RESET_STATE,
  fetchPaymentsMade,
  fetchPaymentsMadeSuccess,
  fetchPaymentsMadeErrorFn,
  fetchPaymentsReceived,
  fetchPaymentsReceivedSuccess,
  fetchPaymentsReceivedErrorFn,
  resetPaymentsState,
};

export const initialState: PaymentsState = {
  paymentsMadePage: 0,
  paymentsReceivedPage: 0,
};

const paginator = new Paginator(['paymentsMade', 'paymentsReceived']);

//Reducer
export default function reducer(state: PaymentsState = initialState, action: PaymentsAction): PaymentsState {
  switch (action.type) {
    case PAYMENT_FETCH_PAYMENTS_MADE_REQUEST:
      if (action.resetPagination) {
        paginator.reset('paymentsMade');
        return {
          isFetchingPaymentsMade: true,
          paymentsMade: paginator.getItems('paymentsMade'),
          paymentsMadePage: paginator.getPage('paymentsMade'),
          paymentsMadePageCanAsk: paginator.getCanAsk('paymentsMade'),
        };
      }
      return {
        ...state,
        isFetchingPaymentsMade: true,
      };
    case PAYMENT_FETCH_PAYMENTS_MADE_SUCCESS:
      paginator.increment('paymentsMade', action.paymentsMade, state.paymentsMade);
      return {
        paymentsMade: paginator.getItems('paymentsMade'),
        paymentsMadePage: paginator.getPage('paymentsMade'),
        paymentsMadePageCanAsk: paginator.getCanAsk('paymentsMade'),
      };
    case PAYMENT_FETCH_PAYMENTS_MADE_ERROR:
      return {
        fetchPaymentsMadeError: action.fetchPaymentsMadeError,
        paymentsMadePage: state.paymentsMadePage,
      };
    case PAYMENT_FETCH_PAYMENTS_RECEIVED_REQUEST:
      if (action.resetPagination) paginator.reset('paymentsReceived');
      return {
        isFetchingPaymentsReceived: true,
        paymentsReceived: paginator.getItems('paymentsReceived'),
        paymentsReceivedPage: paginator.getPage('paymentsReceived'),
        paymentsReceivedPageCanAsk: paginator.getCanAsk('paymentsReceived'),
      };
    case PAYMENT_FETCH_PAYMENTS_RECEIVED_SUCCESS:
      paginator.increment('paymentsReceived', action.paymentsReceived, state.paymentsReceived);
      return {
        paymentsReceived: paginator.getItems('paymentsReceived'),
        paymentsReceivedPage: paginator.getPage('paymentsReceived'),
        paymentsReceivedPageCanAsk: paginator.getCanAsk('paymentsReceived'),
      };
    case PAYMENT_FETCH_PAYMENTS_RECEIVED_ERROR:
      return {
        fetchPaymentsReceivedError: action.fetchPaymentsReceivedError,
        paymentsReceivedPage: state.paymentsReceivedPage,
      };
    case PAYMENT_RESET_STATE:
      paginator.resetAll();
      return initialState;
    default:
      return state;
  }
}

// Epics
export function fetchPaymentsMadeEpic(action$: Observable<PaymentsAction>,
                                      state$: StateObservable<RootState>): Observable<PaymentsAction> {
  return action$.pipe(
    ofType(PAYMENT_FETCH_PAYMENTS_MADE_REQUEST),
    debounceTime(600),
    switchMap(({ queryMadeFilter }) => {
        return getPaymentsMadeApi({
          ...queryMadeFilter,
          page: state$.value.payments.paymentsMadePage || paginator.getPage('paymentsMade'),
          limit: paginator.getLimit(),
        }).pipe(
          takeUntil(action$.pipe(ofType(PAYMENT_RESET_STATE))),
          takeUntil(action$.pipe(ofType(PAYMENT_FETCH_PAYMENTS_MADE_REQUEST))),
          map((response: Invoice[]) => fetchPaymentsMadeSuccess(response)),
          catchError((e: HTTPError) => {
            if (Math.round(e.code / 100) === 5) {
              return of(fetchPaymentsMadeErrorFn(e),
                setNewAlert('payments', 'error', undefined, 'httpErrors.fetch.500'),
              );
            } else if (e.code === 400) {
              return of(fetchPaymentsMadeErrorFn(e),
                setNewAlert('payments', 'error', undefined, 'httpErrors.fetch.400'),
              );
            }
            return of(fetchPaymentsMadeErrorFn(e))
          }));
      }
    ));
}

export function fetchPaymentsReceivedEpic(action$: Observable<PaymentsAction>,
                                          state$: StateObservable<RootState>): Observable<PaymentsAction> {
  return action$.pipe(
    ofType(PAYMENT_FETCH_PAYMENTS_RECEIVED_REQUEST),
    debounceTime(600),
    switchMap(({ queryReceivedFilter }) => getPaymentsReceivedApi({
        ...queryReceivedFilter,
        page: state$.value.payments.paymentsReceivedPage || paginator.getPage('paymentsReceived'),
        limit: paginator.getLimit(),
      }).pipe(
      takeUntil(action$.pipe(ofType(PAYMENT_RESET_STATE))),
      takeUntil(action$.pipe(ofType(PAYMENT_FETCH_PAYMENTS_RECEIVED_REQUEST))),
      map((response: Invoice[]) => fetchPaymentsReceivedSuccess(response)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchPaymentsReceivedErrorFn(e),
            setNewAlert('payments', 'error', undefined, 'httpErrors.fetch.500'),
          );
        } else if (e.code === 400) {
          return of(fetchPaymentsReceivedErrorFn(e),
            setNewAlert('payments', 'error', undefined, 'httpErrors.fetch.400'),
          );
        }
        return of(fetchPaymentsReceivedErrorFn(e))
      }))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/payments/duck.js